import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Autocomplete,
  Divider,
  Grid,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  SyncAlt as SyncAltIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useLead } from '../../contexts/LeadContext';
import { useModal } from '../../contexts/ModalContext';
import { fetchStageStatusFlow, fetchLeadNoteTemplates } from '../../services/common';

const T = {
  green: '#0a6b3e',
  greenLt: '#e5f5ec',
  greenMd: '#0d8a50',
  greenSf: 'rgba(10,107,62,0.06)',
  ink: '#0f1a13',
  ink2: '#253b2c',
  subtle: '#526b5c',
  muted: '#8eaa98',
  bg: '#f5f8f6',
  border: '#dce8e1',
  borderL: '#eaf1ec',
  white: '#ffffff',
  red: '#dc2626',
  redLt: '#fef2f2',
  redBorder: '#fecaca',
  font: "'Inter','DM Sans','Plus Jakarta Sans',sans-serif",
  stageCardBg: '#e5f5ec',
  stageCardBorder: '#c5e6d5',
  stageCardAccent: '#0a6b3e',
  statusCardBg: '#f3f0ff',
  statusCardBorder: '#ddd6fe',
  statusCardAccent: '#5b21b6',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: T.font,
    fontSize: 13,
    borderRadius: 2,
    bgcolor: T.white,
    '& fieldset': { borderColor: T.borderL },
    '&:hover fieldset': { borderColor: T.border },
    '&.Mui-focused fieldset': { borderColor: T.green },
  },
  '& .MuiInputLabel-root': {
    fontFamily: T.font,
    fontSize: 13,
    color: T.subtle,
    '&.Mui-focused': { color: T.green },
  },
};

const colorForStage = (stage) => {
  if (!stage) return { color: T.ink, bg: 'transparent' };
  const s = stage.toLowerCase();
  if (['new'].includes(s)) return { color: '#1e40af', bg: '#eff6ff' };
  if (['contacted'].includes(s)) return { color: '#1e40af', bg: '#eff6ff' };
  if (['qualified', 'negotiation'].includes(s)) return { color: '#92400e', bg: '#fffbeb' };
  if (['closure'].includes(s)) return { color: T.green, bg: T.greenLt };
  if (['junk'].includes(s)) return { color: '#991b1b', bg: T.redLt };
  return { color: T.ink, bg: 'transparent' };
};

const colorForStatus = (status) => {
  if (!status) return { color: T.ink, bg: 'transparent' };
  const s = status.toLowerCase();
  if (['new', 'unassigned', 'assigned', 'reassigned', 'contacted', 'follow_up_scheduled', 'ringing'].includes(s)) return { color: '#1e40af', bg: '#eff6ff' };
  if (['qualified', 'interested', 'property_shared', 'site_visit_scheduled', 'visited', 'closed_won'].includes(s)) return { color: T.green, bg: T.greenLt };
  if (['shortlisted', 'offer_made'].includes(s)) return { color: '#92400e', bg: '#fffbeb' };
  if (['lost', 'not_interested', 'cold_lead', 'unreachable', 'no_response', 'no_show', 'not_looking_currently', 'junk'].includes(s)) return { color: '#991b1b', bg: T.redLt };
  return { color: T.ink, bg: 'transparent' };
};

const LeadBar = ({ leadId, leadName, stageLabel, statusLabel, stage, status }) => {
  const stageColors = colorForStage(stage);
  const statusColors = colorForStatus(status);

  const items = [
    { label: 'ID', value: leadId ? `#${leadId}` : '—' },
    { label: 'Name', value: leadName || '—', bold: true },
    { label: 'Stage', value: stageLabel || '—', valueColor: stageColors.color, valueBg: stageColors.bg },
    { label: 'Status', value: statusLabel || '—', valueColor: statusColors.color, valueBg: statusColors.bg },
  ];

  return (
    <Box
      sx={{
        mx: 2.5,
        mt: 2,
        mb: 0,
        display: 'flex',
        bgcolor: T.bg,
        borderRadius: 2,
        border: `1px solid ${T.borderL}`,
      }}
    >
      {items.map((item, idx) => (
        <Box
          key={item.label}
          sx={{
            px: 1.75,
            py: 1.25,
            borderRight: idx < items.length - 1 ? `1px solid ${T.borderL}` : 'none',
            minWidth: 0,
            flex: idx === 1 ? '1 1 0%' : '0 0 auto',
          }}
        >
          <Typography sx={{ fontFamily: T.font, fontSize: 10, fontWeight: 600, color: T.muted, letterSpacing: '0.04em', lineHeight: 1, mb: 0.5 }}>
            {item.label}
          </Typography>
          {item.valueBg ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1,
                py: '2px',
                borderRadius: '12px',
                bgcolor: item.valueBg,
              }}
            >
              <Typography
                sx={{
                  fontFamily: T.font,
                  fontSize: 12,
                  fontWeight: 600,
                  color: item.valueColor,
                  lineHeight: 1.3,
                  whiteSpace: 'nowrap',
                }}
              >
                {item.value}
              </Typography>
            </Box>
          ) : (
            <Typography
              sx={{
                fontFamily: T.font,
                fontSize: 13,
                fontWeight: item.bold ? 600 : 500,
                color: T.ink,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.value}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

const PillSelector = ({ options, value, onChange, isJunk }) => (
  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
    {options.map((opt) => {
      const active = value?.value === opt.value;
      const isJunkPill = isJunk && opt.value === 'junk';
      return (
        <Box
          key={opt.value}
          component="button"
          type="button"
          onClick={() => onChange(opt)}
          sx={{
            px: 1.75,
            py: '6px',
            borderRadius: '20px',
            border: `1px solid ${active ? (isJunkPill ? T.red : T.green) : T.borderL}`,
            fontSize: 12,
            fontFamily: T.font,
            cursor: 'pointer',
            transition: 'all 0.15s',
            ...(active
              ? {
                  background: isJunkPill ? T.red : T.green,
                  borderColor: isJunkPill ? T.red : T.green,
                  color: '#fff',
                  fontWeight: 600,
                }
              : {
                  background: T.white,
                  color: T.subtle,
                  '&:hover': {
                    borderColor: T.green,
                    background: T.greenSf,
                    color: T.green,
                  },
                }),
          }}
        >
          {opt.label}
        </Box>
      );
    })}
  </Box>
);

const ContextCards = ({ stageLabel, statusLabel }) => {
  const Card_ = ({ eyebrow, value, variant }) => {
    const isStage = variant === 'stage';
    const bg = isStage ? T.stageCardBg : T.statusCardBg;
    const border = isStage ? T.stageCardBorder : T.statusCardBorder;
    const eye = isStage ? T.stageCardAccent : T.statusCardAccent;
    return (
      <Box
        sx={{
          borderRadius: 2,
          p: 1.75,
          background: bg,
          border: `1px solid ${border}`,
        }}
      >
        <Typography
          sx={{
            fontFamily: T.font,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.04em',
            color: eye,
            mb: 1,
          }}
        >
          {eyebrow}
        </Typography>
        <Typography sx={{ fontFamily: T.font, fontSize: 15, fontWeight: 600, color: T.ink, lineHeight: 1.35 }}>
          {value}
        </Typography>
      </Box>
    );
  };

  return (
    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      <Grid item xs={12} sm={6}>
        <Card_ eyebrow="Next Stage" value={stageLabel} variant="stage" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Card_ eyebrow="Next Status" value={statusLabel} variant="status" />
      </Grid>
    </Grid>
  );
};

const UpdateStatus = ({
  open,
  onClose,
  leadIds = [],
  currentStatus = null,
  currentStage = null,
  leadName = '',
  leadId = '',
  projectUnits = [],
  onComplete = () => {},
  allowAllStages = false,
  targetStage = null,
}) => {
  const { updateLeadStatus } = useLead();
  const [leadNoteTemplates, setLeadNoteTemplates] = useState([]);
  const [stageStatusFlow, setStageStatusFlow] = useState([]);
  const { openSuccessModal, openErrorModal } = useModal();

  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedNote, setSelectedNote] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [statusOptions, setStatusOptions] = useState([]);
  const [noteOptions, setNoteOptions] = useState([]);
  const [error, setError] = useState(null);
  const [showBookingPrompt, setShowBookingPrompt] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);

  const formatLabel = (label) => {
    if (!label) return '';
    return label.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getCurrentStageLabel = () => {
    const stage = stageStatusFlow.find((s) => s.stage === currentStage);
    return stage ? formatLabel(stage.label) : formatLabel(currentStage);
  };

  const getCurrentStatusLabel = () => {
    const stage = stageStatusFlow.find((s) => s.stage === currentStage);
    if (!stage) return formatLabel(currentStatus);
    const status = stage.statuses.find((s) => s.status === currentStatus);
    return status ? status.label : formatLabel(currentStatus);
  };

  const getCurrentStageOrder = () => {
    const stage = stageStatusFlow.find((s) => s.stage === currentStage);
    return stage ? stage.order : -1;
  };

  const getCurrentStatusOrder = () => {
    const stage = stageStatusFlow.find((s) => s.stage === currentStage);
    if (!stage) return -1;
    const status = stage.statuses.find((s) => s.status === currentStatus);
    return status ? status.order : -1;
  };

  const getAvailableStages = () => {
    if (allowAllStages) {
      return stageStatusFlow.map((stage) => ({
        value: stage.stage,
        label: formatLabel(stage.label || stage.stage),
        order: stage.order,
      }));
    }
    if (targetStage) {
      const targetStageData = stageStatusFlow.find((s) => s.stage === targetStage);
      if (targetStageData) {
        return [{
          value: targetStageData.stage,
          label: formatLabel(targetStageData.label || targetStageData.stage),
          order: targetStageData.order,
        }];
      }
      return [];
    }
    const currentOrder = getCurrentStageOrder();
    return stageStatusFlow
      .filter((stage) => stage.order >= currentOrder)
      .map((stage) => ({
        value: stage.stage,
        label: formatLabel(stage.label || stage.stage),
        order: stage.order,
      }));
  };

  const getStatusesForStage = (stageValue) => {
    const stage = stageStatusFlow.find((s) => s.stage === stageValue);
    if (!stage) return [];
    if (stageValue === currentStage) {
      const currentOrder = getCurrentStatusOrder();
      return stage.statuses
        .filter((status) => status.order >= currentOrder)
        .map((status) => ({
          value: status.status,
          label: status.label || formatLabel(status.status),
          order: status.order,
        }));
    }
    return stage.statuses.map((status) => ({
      value: status.status,
      label: status.label || formatLabel(status.status),
      order: status.order,
    }));
  };

  const hasStageOrStatusChanged = () => {
    if (!selectedStage || !selectedStatus) return false;
    if (targetStage) return true;
    return selectedStage.value !== currentStage || selectedStatus.value !== currentStatus;
  };

  useEffect(() => {
    if (open) {
      let stageToUse = currentStage;
      let statusToUse = currentStatus;

      if (targetStage) {
        stageToUse = targetStage;
        if (targetStage === 'qualified') statusToUse = 'interested';
      }

      if (stageToUse && stageStatusFlow?.length > 0) {
        const stageData = stageStatusFlow.find((s) => s.stage === stageToUse);
        if (stageData) {
          const stageDataObj = {
            value: stageData.stage,
            label: formatLabel(stageData.label || stageData.stage),
            order: stageData.order,
          };
          setSelectedStage(stageDataObj);

          const stageStatuses = getStatusesForStage(stageDataObj.value);
          setStatusOptions(stageStatuses);

          if (statusToUse) {
            const currentStatusData = stageStatuses.find((s) => s.value === statusToUse);
            if (currentStatusData) setSelectedStatus(currentStatusData);
            else if (stageStatuses.length > 0) setSelectedStatus(stageStatuses[0]);
          } else if (stageStatuses.length > 0) {
            setSelectedStatus(stageStatuses[0]);
          }
        }

        if (leadNoteTemplates?.[stageToUse]) {
          const stageNotes = [...leadNoteTemplates[stageToUse]];
          setNoteOptions(stageNotes);
          if (stageNotes.length > 0) setSelectedNote(stageNotes[0]);
        } else {
          setNoteOptions([]);
          setSelectedNote('');
        }
      } else if (stageStatusFlow?.length > 0) {
        const firstStage = {
          value: stageStatusFlow[0].stage,
          label: formatLabel(stageStatusFlow[0].label || stageStatusFlow[0].stage),
          order: stageStatusFlow[0].order,
        };
        setSelectedStage(firstStage);

        const stageStatuses = stageStatusFlow[0].statuses.map((status) => ({
          value: status.status,
          label: status.label || formatLabel(status.status),
          order: status.order,
        }));
        setStatusOptions(stageStatuses);
        if (stageStatuses.length > 0) setSelectedStatus(stageStatuses[0]);

        if (leadNoteTemplates?.[firstStage.value]) {
          const stageNotes = [...leadNoteTemplates[firstStage.value]];
          setNoteOptions(stageNotes);
          if (stageNotes.length > 0) setSelectedNote(stageNotes[0]);
        }
      }

      setAdditionalNotes('');
      setError(null);
      setSelectedProject(null);
      setSelectedUnit(null);

      if (!stageStatusFlow?.length) fetchStageStatusFlow().then(setStageStatusFlow);
      if (!leadNoteTemplates || Object.keys(leadNoteTemplates).length === 0) {
        fetchLeadNoteTemplates().then(setLeadNoteTemplates);
      }
    }
  }, [open, currentStage, currentStatus, stageStatusFlow, leadNoteTemplates, targetStage]);

  useEffect(() => {
    if (selectedStage && leadNoteTemplates) {
      const stage = selectedStage.value;
      const newNoteOptions = leadNoteTemplates[stage] ? [...leadNoteTemplates[stage]] : [];
      setNoteOptions(newNoteOptions);
      if (newNoteOptions.length > 0) setSelectedNote(newNoteOptions[0]);
      else setSelectedNote('');
    }
  }, [selectedStage, leadNoteTemplates]);

  useEffect(() => {
    if (selectedStage) {
      const options = getStatusesForStage(selectedStage.value);
      setStatusOptions(options);
      const statusExists = selectedStatus && options.some((option) => option.value === selectedStatus.value);
      if (!statusExists && options.length > 0) setSelectedStatus(options[0]);
      else if (!statusExists) setSelectedStatus(null);
    }
  }, [selectedStage]);

  useEffect(() => {
    if (selectedStage && selectedStatus) {
      const statusValue = selectedStatus.value.toLowerCase();
      setShowBookingPrompt(
        selectedStage.value.toLowerCase() === 'closure' &&
        (statusValue === 'won' || statusValue === 'closed won' || statusValue === 'closed_won')
      );
    } else {
      setShowBookingPrompt(false);
    }
  }, [selectedStage, selectedStatus]);

  const handleSubmit = async () => {
    if (!selectedStage || !selectedStatus || !selectedNote) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const leadUpdates = leadIds.map((id) => {
        const updateObj = {
          lead_id: id,
          new_stage: selectedStage.value,
          new_status: selectedStatus.value,
          notes: selectedNote,
        };
        if (showBookingPrompt) {
          updateObj.booking_draft = {
            project_id: selectedProject?.value || selectedProject?.id || null,
            unit_id: selectedUnit?.value || selectedUnit?.id || null,
          };
        }
        return updateObj;
      });

      if (additionalNotes.trim() && leadUpdates.length > 0) {
        leadUpdates[0].notes = `${selectedNote}\n${additionalNotes.trim()}`;
      }

      const result = await updateLeadStatus(leadUpdates);

      if (result.passed?.length > 0) {
        onComplete();
        onClose();
        openSuccessModal(
          'Status updated',
          `Successfully updated ${result.passed.length} lead(s)${showBookingPrompt ? ' and created booking draft' : ''}`
        );
      }
      if (result.errored?.length > 0) {
        openErrorModal('Update failed', `Failed to update ${result.errored.length} lead(s)`);
      }
    } catch (err) {
      setError(err.message || 'Failed to update status');
    }
  };

  const availableStages = getAvailableStages();
  const changed = hasStageOrStatusChanged();
  const submitDisabled = !selectedStage || !selectedStatus || !selectedNote || !availableStages.length || !changed;

  const headerTitle = targetStage ? `Move to ${formatLabel(targetStage)}` : 'Update Lead Status';
  const headerHint = targetStage ? 'Confirm Stage, Status, and Note' : 'Change Pipeline Stage and Status';

  const eyebrowSx = {
    fontFamily: T.font,
    fontSize: 10.5,
    fontWeight: 700,
    color: T.subtle,
    letterSpacing: '0.05em',
    mb: 1,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 600,
          width: '100%',
          borderRadius: 3,
          fontFamily: T.font,
          overflow: 'hidden',
          m: 2,
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: T.greenLt,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <SyncAltIcon sx={{ fontSize: 22, color: T.green }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: T.font, fontSize: 16, fontWeight: 700, color: T.ink, lineHeight: 1.3 }}>
                {headerTitle}
              </Typography>
              <Typography sx={{ fontFamily: T.font, fontSize: 11.5, color: T.muted, mt: 0.25 }}>
                {headerHint}
              </Typography>
              {leadIds.length > 1 && (
                <Typography sx={{ fontFamily: T.font, fontSize: 11, color: T.muted, mt: 0.5 }}>
                  {leadIds.length} leads selected
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ color: T.muted, '&:hover': { color: T.ink } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: T.borderL }} />

      <LeadBar
        leadId={leadId}
        leadName={leadName}
        stageLabel={getCurrentStageLabel()}
        statusLabel={getCurrentStatusLabel()}
        stage={currentStage}
        status={currentStatus}
      />

      <DialogContent sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Typography sx={eyebrowSx}>Pipeline Stage</Typography>
        {availableStages.length > 0 ? (
          <PillSelector
            options={availableStages}
            value={selectedStage}
            onChange={(opt) => {
              setSelectedStage(opt);
              setSelectedStatus(null);
            }}
            isJunk
          />
        ) : (
          <Typography sx={{ fontFamily: T.font, fontSize: 12, color: '#d97706' }}>No stages available for this lead.</Typography>
        )}

        <Divider sx={{ my: 2, borderColor: T.borderL }} />

        <Typography sx={eyebrowSx}>Status</Typography>
        {statusOptions.length > 0 ? (
          <PillSelector options={statusOptions} value={selectedStatus} onChange={setSelectedStatus} />
        ) : (
          <Typography sx={{ fontFamily: T.font, fontSize: 12, color: T.muted }}>
            {selectedStage ? 'No Statuses Available.' : 'Select a Stage First.'}
          </Typography>
        )}

        <Divider sx={{ my: 2, borderColor: T.borderL }} />

        <ContextCards
          stageLabel={selectedStage?.label || '—'}
          statusLabel={selectedStatus?.label || 'Select Status'}
        />

        {showBookingPrompt && (
          <Box
            sx={{
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${T.border}`,
              bgcolor: T.greenLt,
              p: 2,
            }}
          >
            <Typography sx={{ fontFamily: T.font, fontSize: 12.5, fontWeight: 600, color: T.green, mb: 1.5 }}>
              A draft booking will be created automatically. Project and unit are optional.
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  value={selectedProject}
                  onChange={(_, v) => {
                    setSelectedProject(v);
                    setSelectedUnit(null);
                  }}
                  options={projectUnits || []}
                  getOptionLabel={(o) => o.label || o.name || ''}
                  renderInput={(params) => (
                    <TextField {...params} label="Project (optional)" helperText="Can be added later" size="small" sx={fieldSx} />
                  )}
                  isOptionEqualToValue={(o, v) => (o.id || o.value) === (v?.id || v?.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  value={selectedUnit}
                  onChange={(_, v) => setSelectedUnit(v)}
                  options={selectedProject ? selectedProject.units || [] : []}
                  getOptionLabel={(o) => o.label || o.name || o.unit_number || ''}
                  disabled={!selectedProject}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Unit (optional)"
                      helperText={selectedProject ? 'Can be added later' : 'Select project first'}
                      size="small"
                      disabled={!selectedProject}
                      sx={fieldSx}
                    />
                  )}
                  isOptionEqualToValue={(o, v) => (o.id || o.value) === (v?.id || v?.value)}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        <Autocomplete
          value={selectedNote}
          onChange={(_, v) => setSelectedNote(v)}
          options={noteOptions}
          getOptionLabel={(o) => o}
          disableClearable
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Note"
              required
              error={!selectedNote && !!error}
              helperText={!selectedNote && error ? 'Required' : ''}
              size="small"
              sx={{ ...fieldSx, mb: 1.5 }}
            />
          )}
          sx={{ mb: 0.5 }}
        />

        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          label="Additional Notes"
          value={additionalNotes}
          onChange={(e) => setAdditionalNotes(e.target.value)}
          placeholder="Optional — extra detail for the activity timeline"
          sx={fieldSx}
        />

        {error && typeof error === 'string' && (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, border: `1px solid ${T.redBorder}`, bgcolor: T.redLt }}>
            <Typography sx={{ fontFamily: T.font, fontSize: 12.5, color: T.red }}>{error}</Typography>
          </Box>
        )}
        {selectedStage && selectedStatus && !changed && !targetStage && (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, border: `1px solid ${T.redBorder}`, bgcolor: T.redLt }}>
            <Typography sx={{ fontFamily: T.font, fontSize: 12.5, color: T.red }}>
              Select a different stage or status to proceed with the update.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1,
          justifyContent: 'flex-end',
          borderTop: `1px solid ${T.borderL}`,
          bgcolor: T.bg,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 500,
            color: T.subtle,
            textTransform: 'none',
            borderRadius: 2,
            px: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitDisabled}
          endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            px: 2.5,
            bgcolor: T.green,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: T.borderL, color: T.muted },
          }}
        >
          Update status
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateStatus;
